/**
 * Fret-seventh recognition — guitar rung 8.
 *
 * A key-establishing cadence plays, then a 7th chord (maj7, min7, or dom7).
 * The CAGED shape region is shown with the 3rd, 5th, and 7th as `given` dots;
 * the user taps the root on the canonical string.
 *
 * New over rungs 4–6: three given notes instead of two. The student must orient
 * from three chord tones to find the root — the 7th adds a distinctive colour
 * that narrows down which note is missing.
 *
 * Key context mirrors rung 4 (CAGED recognition): maj7/dom7 → major key cadence,
 * min7 → harmonic minor cadence.
 *
 * See docs/design/fret-seventh-voicings.md for the voicing spec.
 */

import { AudioPlanBuilder, type AudioPlan } from "./audio-plan.js";
import { Rng, type Exercise, type FretboardPrompt } from "./exercise.js";
import { buildChord, smoothVoicing, type CadenceOptions } from "./utils.js";
import {
  STANDARD_TUNING,
  CAGED_SHAPES,
  midiAt,
  type FretPosition,
  type CagedShape,
} from "@music-app/theory-engine";

// ─── Types ────────────────────────────────────────────────────────────────────

export type SeventhQuality = "maj7" | "min7" | "dom7";

export interface FretSeventhParams {
  shape: CagedShape;
  quality: SeventhQuality;
}

type SeventhVoiceRole = "root" | "third" | "fifth" | "seventh";

interface SeventhVoice {
  /** Octave-encoded semitone offset from root (same semantics as CagedVoice.semitones). */
  semitones: number;
  role: SeventhVoiceRole;
}

// ─── 7th chord CAGED voicing patterns ─────────────────────────────────────────

/**
 * Per-shape patterns for 7th chord voicings.
 *
 * Each map entry is [stringIndex, SeventhVoice].
 * Semitone values are encoded the same way as CagedVoice.semitones:
 *   - The resolver computes octaveBase = Math.floor(semitones/12)*12
 *   - For "third": targetMidi = root + octaveBase + thirdInterval(quality)
 *   - For "seventh": targetMidi = root + octaveBase + seventhInterval(quality)
 *   - For "root"/"fifth": targetMidi = root + semitones (direct)
 *
 * See docs/design/fret-seventh-voicings.md §5 for derivation.
 */
const SEVENTH_PATTERNS: Record<CagedShape, ReadonlyMap<number, SeventhVoice>> = {
  // E-shape: root=str0, fifth=str1(+7), seventh=str2(+10/+11), third=str3(+16/+15)
  E: new Map([
    [1, { semitones: 7,  role: "fifth"   }],
    [2, { semitones: 10, role: "seventh" }],
    [3, { semitones: 16, role: "third"   }],
  ]),
  // A-shape: root=str1, fifth=str2(+7), seventh=str3(+10/+11), third=str4(+16/+15)
  A: new Map([
    [2, { semitones: 7,  role: "fifth"   }],
    [3, { semitones: 10, role: "seventh" }],
    [4, { semitones: 16, role: "third"   }],
  ]),
  // G-shape: root=str0, fifth=str2(+7), third=str4(+16/+15), seventh=str5(+22/+23)
  // Seventh on high-e string keeps everything within 4 frets of root.
  G: new Map([
    [2, { semitones: 7,  role: "fifth"   }],
    [4, { semitones: 16, role: "third"   }],
    [5, { semitones: 22, role: "seventh" }],
  ]),
  // C-shape: root=str4(B string), third=str2(-8/-9), seventh=str3(-2/-1), fifth=str5(+7)
  // Third and seventh sit one octave below root in pitch (negative offsets).
  C: new Map([
    [2, { semitones: -8, role: "third"   }],
    [3, { semitones: -2, role: "seventh" }],
    [5, { semitones:  7, role: "fifth"   }],
  ]),
  // D-shape: same sub-octave layout as C-shape (root on B string).
  D: new Map([
    [2, { semitones: -8, role: "third"   }],
    [3, { semitones: -2, role: "seventh" }],
    [5, { semitones:  7, role: "fifth"   }],
  ]),
};

// ─── Voice resolver ───────────────────────────────────────────────────────────

/**
 * Resolve the MIDI pitch of a seventh-chord voice given the chord root.
 * Parallel to `resolveVoiceMidi` from exercise-fret-caged.ts but extends it
 * to handle the "seventh" role with quality-dependent intervals.
 */
function resolveSeventhVoiceMidi(
  rootMidi: number,
  voice: SeventhVoice,
  quality: SeventhQuality,
): number {
  if (voice.role === "third") {
    const thirdInterval = quality === "min7" ? 3 : 4; // min7 → minor 3rd; maj7/dom7 → major 3rd
    const octaveBase = Math.floor(voice.semitones / 12) * 12;
    return rootMidi + octaveBase + thirdInterval;
  }
  if (voice.role === "seventh") {
    const seventhInterval = quality === "maj7" ? 11 : 10; // maj7 → major 7th; dom7/min7 → minor 7th
    const octaveBase = Math.floor(voice.semitones / 12) * 12;
    return rootMidi + octaveBase + seventhInterval;
  }
  return rootMidi + voice.semitones; // root and fifth are quality-independent
}

// ─── Key helpers ──────────────────────────────────────────────────────────────

const PC_NAMES = ["C","D♭","D","E♭","E","F","F♯","G","A♭","A","B♭","B"] as const;

interface KeyInfo {
  tonicMidi: number;
  name: string;
}

function buildKeyForSeventh(rootMidi: number, quality: SeventhQuality): KeyInfo {
  const pc = ((rootMidi % 12) + 12) % 12;
  const pcName = PC_NAMES[pc]!;
  const accSym = pcName.includes("♭") ? "♭" : pcName.includes("♯") ? "♯" : "";
  const letter = pcName.replace(/[♭♯]/u, "");
  const normalTonic = 60 + pc;
  const modeName = quality === "min7" ? "minor" : "major";
  return { tonicMidi: normalTonic, name: `${letter}${accSym} ${modeName}` };
}

// ─── Cadence builder ──────────────────────────────────────────────────────────

function appendSeventhCadence(
  builder: AudioPlanBuilder,
  rootMidi: number,
  quality: SeventhQuality,
  opts: CadenceOptions,
): void {
  const dur = opts.chordDuration ?? 0.85;
  const gap = opts.gap ?? 0.05;
  const gain = opts.gain ?? 0.07;
  const bassGain = opts.bassGain ?? 0.11;

  const normalTonic = 60 + ((rootMidi % 12) + 12) % 12;

  // maj7/dom7: I–IV–V–I in major.  min7: i–iv–V–i in harmonic minor (V=maj).
  const degrees: Array<{ offset: number; chordQuality: "maj" | "min" }> = quality === "min7"
    ? [
        { offset: 0, chordQuality: "min" },
        { offset: 5, chordQuality: "min" },
        { offset: 7, chordQuality: "maj" },
        { offset: 0, chordQuality: "min" },
      ]
    : [
        { offset: 0, chordQuality: "maj" },
        { offset: 5, chordQuality: "maj" },
        { offset: 7, chordQuality: "maj" },
        { offset: 0, chordQuality: "maj" },
      ];

  let prevVoicing: number[] | null = null;
  for (const { offset, chordQuality } of degrees) {
    const degRoot = normalTonic + offset;
    const triad = buildChord(degRoot, chordQuality);
    const voicing = smoothVoicing(prevVoicing, triad);
    prevVoicing = voicing;
    builder.addNote(degRoot - 12, dur, bassGain, "bass");
    builder.addChord(voicing, dur, gain, "harmony");
    builder.advance(dur + gap);
  }
  builder.advance(0.25);
}

// ─── Public generator ─────────────────────────────────────────────────────────

export function generateFretSeventh(
  itemId: string,
  params: FretSeventhParams,
  seed: number,
  cadenceOpts?: CadenceOptions,
): Exercise {
  const rng = new Rng(seed);

  const shapeDef = CAGED_SHAPES[params.shape];
  if (!shapeDef) {
    throw new Error(`CAGED shape not implemented: ${params.shape}`);
  }

  const seventhPattern = SEVENTH_PATTERNS[params.shape];
  const { canonicalRootString } = shapeDef;
  const minRootFret = shapeDef.minRootFret ?? 2;

  // Root fret for the chord in [minRootFret, 12].
  const rootFret = minRootFret + rng.int(12 - minRootFret + 1);
  const rootMidi = midiAt(STANDARD_TUNING, { stringIndex: canonicalRootString, fret: rootFret });

  const key = buildKeyForSeventh(rootMidi, params.quality);

  // Chord audio (up one octave for clarity).
  const chordAudioRoot = rootMidi + 12;
  const chordMidis = buildChord(chordAudioRoot, params.quality);
  const [chordRoot, chordThird, chordFifth, chordSeventh] = chordMidis as [number, number, number, number];

  // Resolve all voice positions.
  interface VoicePos { stringIndex: number; fret: number; voice: SeventhVoice; }
  const voicePositions: VoicePos[] = [];
  for (const [stringIndex, voice] of seventhPattern) {
    const targetMidi = resolveSeventhVoiceMidi(rootMidi, voice, params.quality);
    const openMidi = midiAt(STANDARD_TUNING, { stringIndex, fret: 0 });
    const fret = targetMidi - openMidi;
    if (fret < 0 || fret > STANDARD_TUNING.fretCount) continue;
    voicePositions.push({ stringIndex, fret, voice });
  }

  // Root position.
  const rootPos: FretPosition = { stringIndex: canonicalRootString, fret: rootFret };

  // Dynamic fret window.
  let minFret = rootFret;
  let maxFret = rootFret;
  for (const vp of voicePositions) {
    minFret = Math.min(minFret, vp.fret);
    maxFret = Math.max(maxFret, vp.fret);
  }
  const fretWindow = {
    from: Math.max(0, minFret - 1),
    to: Math.min(STANDARD_TUNING.fretCount, maxFret + 1),
  };

  // Partition: all non-root voices → given; root on canonical string → acceptedPositions.
  const given: FretPosition[] = voicePositions.map(({ stringIndex, fret }) => ({ stringIndex, fret }));
  const solution: FretPosition[] = [rootPos, ...given];

  const fretboard: FretboardPrompt = {
    tuning: "standard",
    given,
    allowedStrings: [canonicalRootString],
    notesToPlace: 1,
    acceptedPositions: [rootPos],
    solution,
    listenFretWindow: fretWindow,
  };

  // Audio: cadence → 7th chord arpeggio → block chord.
  const builder = new AudioPlanBuilder();
  appendSeventhCadence(builder, rootMidi, params.quality, cadenceOpts ?? {});
  for (const note of [chordRoot, chordThird, chordFifth, chordSeventh]) {
    builder.addNote(note, 0.55, 0.2, "melody");
    builder.advance(0.3);
  }
  builder.advance(0.35);
  builder.addChord([chordRoot, chordThird, chordFifth, chordSeventh], 1.4, 0.13, "harmony");
  builder.advance(1.5);
  const audio = builder.build();

  const rootPcName = PC_NAMES[((rootMidi % 12) + 12) % 12]!;

  return {
    itemId,
    exerciseType: "fret-seventh-recognition",
    audio,
    prompt: `Tap the ${params.quality} root`,
    options: [],
    correctAnswerId: "",
    meta: {
      keyName: key.name,
      seed,
      explanation: `${rootPcName} ${params.quality} — ${params.shape}-shape in ${key.name}.`,
      distractorCount: 0,
      tonicMidi: rootMidi,
      tonalityMode: params.quality === "min7" ? "minor" : "major",
      teachAudio: buildSeventhTeachPlan(chordRoot, chordThird, chordFifth, chordSeventh),
      fretboard,
      playbackMidis: [chordRoot, chordThird, chordFifth, chordSeventh],
    },
  };
}

function buildSeventhTeachPlan(
  rootMidi: number,
  thirdMidi: number,
  fifthMidi: number,
  seventhMidi: number,
): AudioPlan {
  const builder = new AudioPlanBuilder();
  for (const note of [rootMidi, thirdMidi, fifthMidi, seventhMidi]) {
    builder.addNote(note, 0.8, 0.22, "melody");
    builder.advance(0.4);
  }
  builder.advance(0.3);
  builder.addChord([rootMidi, thirdMidi, fifthMidi, seventhMidi], 1.5, 0.13, "harmony");
  builder.advance(1.6);
  return builder.build();
}
